import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, OnDestroy, AfterViewInit, Renderer2 } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Router } from '@angular/router';
import { TableComponent } from '../table/table.component';
import { LoaderService } from '../../../../../core/shared/services/loader.service';
import { AuthService } from '../../../../../core/services/auth.service';
import { AuthUser } from '../../../../../core/models/auth/auth.interface';
import { Objective } from '../../../../../core/models/objective.model';
import { STATES_OBJECTIVES } from '../../../../../core/shared/constants';
import { ObjectivesService } from '../../../../../core/services/objectives.service';
import { TestsService } from '../../../../../core/services/tests.service';
import { environment } from '../../../../../../environments/environment';

@Component({
  selector: 'app-table-objectives',
  standalone: true,
  imports: [MatTableModule, CommonModule, MatIconModule, MatButtonModule, TableComponent],
  templateUrl: './table-objectives.component.html',
  styleUrl: './table-objectives.component.scss',
  animations: [
    trigger('detailExpand', [
      state('collapsed,void', style({ margin: 0, height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class TableObjectivesComponent implements OnInit, AfterViewInit, OnDestroy {
  private authService: AuthService = inject(AuthService);
  authUser: AuthUser = this.authService.getAuthUser()!;
  private objectivesService: ObjectivesService = inject(ObjectivesService);
  private testService: TestsService = inject(TestsService);
  private loader = inject(LoaderService);
  dataSource: Objective[] | null = null;
  columnsToDisplay = ['Nombre', 'Dimension', 'Inicio', 'Fin', 'Estado', 'Porcentaje', 'options'];
  expandedElement: any | null;
  creationInProgress: boolean = false;
  objectviesStarted: number = 0;
  objectivesPending: number = 0;
  objectivesFinished: number = 0;
  percentagemFinished: number = 0;
  menuOptions = [
    { label: 'Ver Objetivo', value: 1 },
    { label: 'Ver actividades', value: 2 },
    { label: 'Editar', value: 3 },
    { label: 'Eliminar', value: 4 },
  ];
  dataHistory: any[] = [];
  isResponsive: boolean = false;

  // Variables para el análisis de gap
  analysisAreas: any[] = [];
  criticalGapAreas: any[] = [];

  constructor(private router: Router, private renderer: Renderer2) { }

  ngOnInit() {
    this.getObjectives();
    this.loadAnalysisData();
  }

  changeCreationInProgress() {
    this.creationInProgress = !this.creationInProgress;
  }

  calculateObjectives() {
    if (!this.dataSource || this.dataSource.length === 0) {
      this.objectviesStarted = 0;
      this.objectivesPending = 0;
      this.objectivesFinished = 0;
      this.percentagemFinished = 0;
      return;
    }

    this.objectviesStarted = this.dataSource.filter((element) => element.state === "Empezado").length;
    this.objectivesPending = this.dataSource.filter((element) => element.state === "Pendiente" || element.state === "Nuevo" || element.state === "No empezado").length;
    this.objectivesFinished = this.dataSource.filter((element) => element.state === "Terminado" || element.state === "Realizado").length;
    this.percentagemFinished = Math.round(this.objectivesFinished / this.dataSource.length * 100) || 0;
  }

  async getObjectives() {
    this.loader.show();
    this.objectivesService.findAll(this.authUser.id).subscribe({
      next: (res: any) => {
        if (res.data) {
          //console.log('res.data', res.data);
          this.dataSource = res.data;
          this.dataHistory = [...res.data].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          ).slice(0, 6); // Mostrar sólo los 6 más recientes
          this.calculateObjectives();
        }
        this.loader.hide();
      },
      error: (err: any) => {
        //console.log('err', err);
        this.loader.hide();
      }
    });
  }

  eliminarObjetivo(id: string) {
    this.objectivesService.deleteObjective(id).subscribe({
      next: (res: any) => {
        this.dataSource = this.dataSource!.filter((element) => element._id !== id);

        // Actualizar también la lista del historial
        this.dataHistory = this.dataHistory.filter((element) => element._id !== id);

        this.calculateObjectives();
      },
      error: (err: any) => {
        //console.log('Error al eliminar objetivo', err);
      }
    });
  }

  calcularTiempoTranscurrido(fechaISO: string | undefined) {
    if (!fechaISO) {
      return ''; // Si no hay fechaISO, no mostrar nada
    }
    const fecha = new Date(fechaISO);
    if (isNaN(fecha.getTime())) {
      return ''; // Si la fecha parseada es inválida, no mostrar nada
    }

    const ahora = new Date();
    const diferenciaMs = ahora.getTime() - fecha.getTime();
    const minutos = Math.floor(diferenciaMs / 60000);
    const horas = Math.floor(minutos / 60);
    const dias = Math.floor(horas / 24);
    const semanas = Math.floor(dias / 7);

    if (semanas > 0) return `• Hace ${semanas} semanas`;
    if (dias > 0) return `• Hace ${dias} días`;
    if (horas > 0) return `• Hace ${horas} horas`;
    if (minutos >= 0) return `• Hace ${minutos} minutos`; // Incluir "Hace 0 minutos" si es el caso

    return ''; // Fallback por si algo inesperado ocurre
  }

  actualizarDiseno(responsive: boolean) {
    this.isResponsive = responsive;
  }

  // Método para manejar el cambio de estado de un objetivo
  onStateChanged() {
    this.calculateObjectives();
  }

  // Método para recargar la lista de objetivos
  reloadObjectivesList() {
    this.getObjectives();
  }

  // Método para navegar a Academy con la dimensión específica
  navigateToAcademy(areaName: string) {
    // Navegar a /dashboard/academy con el parámetro de dimensión
    this.router.navigate(['/dashboard/academy'], {
      queryParams: { dimension: areaName }
    });
  }

  getCircleStyle() {
    return {
      'background': `conic-gradient(#e30624 ${this.percentagemFinished}%, #E0E0E0 0%)`
    };
  }

  // Método para cargar los datos de análisis
  loadAnalysisData(): void {
    this.testService.find(this.authUser.id).subscribe({
      next: (res: any) => {
        if (res.statusCode && res.data) {
          this.generateAnalysisAreas(res.data);
        }
      },
      error: (error) => {
        console.error('Error cargando datos de análisis:', error);
      }
    });
  }

  // Generar áreas de análisis y obtener las 3 más críticas
  generateAnalysisAreas(tests: any[]): void {
    // Obtener el test del presente (type.id == 2) y futuro (type.id == 3)
    const presentTest = tests.find((x: any) => x.type.id == 2);
    const futureTest = tests.find((x: any) => x.type.id == 3);

    if (presentTest && futureTest) {
      // Crear un mapa de dimensiones para facilitar la búsqueda
      const presentMap = new Map();
      const futureMap = new Map();

      presentTest.questions.forEach((q: any) => {
        presentMap.set(q.dimension.name, parseInt(q.answer?.value || '0'));
      });

      futureTest.questions.forEach((q: any) => {
        futureMap.set(q.dimension.name, parseInt(q.answer?.value || '0'));
      });

      // Generar analysisAreas con los datos reales
      this.analysisAreas = presentTest.questions.map((q: any) => {
        const presentValue = presentMap.get(q.dimension.name) || 0;
        const futureValue = futureMap.get(q.dimension.name) || 0;
        const gap = futureValue - presentValue;

        //console.log(`Calculando gap para "${q.dimension.name}": futuro(${futureValue}) - presente(${presentValue}) = ${gap}`);

        return {
          name: q.dimension.name,
          present: presentValue,
          future: futureValue,
          gap: gap,
          description: this.getDescriptionByGap(q.dimension.name, gap)
        };
      });
      //console.log('analysisAreas', this.analysisAreas);
      // Obtener las 3 áreas con mayor gap (más críticas)
      this.criticalGapAreas = this.analysisAreas
        .filter(area => area.gap > 0) // Solo áreas con gap positivo
        .sort((a, b) => b.gap - a.gap) // Ordenar por gap descendente
        .slice(0, 3); // Tomar las 3 primeras

      //console.log('Áreas críticas del gap:', this.criticalGapAreas);
    }
  }

  // Método para obtener descripción por gap usando environment dinámicamente
  private getDescriptionByGap(areaName: string, gap: number): string {
    const descriptions = environment.gapDescriptions;
    const gapRanges = environment.gapRanges;

    // Asegurar que gap sea un número
    const gapNumber = typeof gap === 'string' ? parseFloat(gap) : gap;

    //console.log('=== DEBUG getDescriptionByGap ===');
    //console.log('areaName recibido:', areaName);
    //console.log('gap recibido:', gap, 'tipo:', typeof gap);
    //console.log('gap convertido a número:', gapNumber);
    //console.log('Claves disponibles en descriptions:', Object.keys(descriptions));

    // Intentar primero con el nombre exacto
    let areaDescriptions = descriptions[areaName as keyof typeof descriptions];

    // Si no se encuentra, intentar con búsqueda case-insensitive
    if (!areaDescriptions) {
      //console.log('⚠️ No se encontró con nombre exacto, intentando búsqueda case-insensitive...');
      const normalizedAreaName = areaName.toLowerCase().trim();
      const foundKey = Object.keys(descriptions).find(key =>
        key.toLowerCase().trim() === normalizedAreaName
      );

      if (foundKey) {
        //console.log('✅ Encontrado con búsqueda normalizada:', foundKey);
        areaDescriptions = descriptions[foundKey as keyof typeof descriptions];
      }
    }

    //console.log('areaDescriptions encontrado:', areaDescriptions);

    if (areaDescriptions) {
      // Buscar el rango dinámico que corresponde al gap
      //console.log('Buscando rango para gap:', gapNumber);
      const matchingRange = gapRanges.find(range => {
        const matches = gapNumber >= range.min && gapNumber <= range.max;
        //console.log(`Comparando gap ${gapNumber} con rango [${range.min}-${range.max}]:`, matches);
        return matches;
      });

      //console.log('matchingRange encontrado:', matchingRange);

      if (matchingRange) {
        const description = areaDescriptions[matchingRange.key as keyof typeof areaDescriptions];
        //console.log('Descripción encontrada:', description);
        if (description) {
          return description;
        }
      }
    } else {
      //console.log('⚠️ NO se encontró areaDescriptions para:', areaName);
      //console.log('Nombres disponibles:', Object.keys(descriptions));
    }

    // Descripción por defecto si no hay gap o no se encuentra la área
    //console.log('❌ Retornando mensaje por defecto');
    return 'Brecha de ' + gapNumber + ' puntos No detectada!!';
  }

  // --- Propiedades del Banner Slider ---
  currentSlide: number = 0;
  private slideInterval: any;

  // --- Métodos del Banner Slider ---
  goToSlide(slideIndex: number): void {
    this.currentSlide = slideIndex;
  }

  nextSlide(): void {
    this.currentSlide = (this.currentSlide + 1) % 3;
  }

  previousSlide(): void {
    this.currentSlide = this.currentSlide === 0 ? 2 : this.currentSlide - 1;
  }

  startAutoSlide(): void {
    this.slideInterval = setInterval(() => {
      this.nextSlide();
    }, 5000); // Cambia cada 5 segundos
  }

  stopAutoSlide(): void {
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
    }
  }

  // Iniciar auto-slide cuando el componente se inicializa
  ngAfterViewInit(): void {
    this.startAutoSlide();
  }

  // Limpiar interval cuando el componente se destruye
  ngOnDestroy(): void {
    this.stopAutoSlide();
  }

  // --- Métodos de Navegación del Banner ---
  navigateToAcademyFromBanner(): void {
    this.router.navigate(['/dashboard/academy']);
  }

  navigateToMentorsFromBanner(): void {
    this.router.navigate(['/dashboard/mentors']);
  }
}

//   {
//     Nombre: 'Cras ornare tristique ',
//     Descripcion: '',
//     Dimension: 'Ingresos económicos',
//     Inicio: '12/02/2025',
//     Fin: '15/02/2025',
//     Estado: `Terminado`,
//     Porcentaje: 90,
//     Actividades: [
//       {
//         Nombre: 'Aliquam tincidunt maur',
//         Descripcion: 'Risus.liquam tincidunt mauri..',
//         Inicio: '22/02/2025',
//         Fin: '25/02/2025',
//         Estado: 'Terminado',
//       },
//       {
//         Nombre: 'Tincidunt mauris eu risus.',
//         Descripcion: '-',
//         Inicio: '25/02/2025',
//         Fin: '26/02/2025',
//         Estado: 'Empezado',
//       },
//     ],
//   },
//   {
//     Nombre: 'Vestibulum auctor dapibus neque.',
//     Descripcion: 'Cras ornare tristique elit Cras ornare tristique elit Cras ornare tristique elit.',
//     Dimension: 'Diversión',
//     Inicio: '03/03/2025',
//     Fin: '05/03/2025',
//     Estado: `Empezado`,
//     Porcentaje: 25,
//     Actividades: [],
//   },
//   {
//     Nombre: 'Cras ornare tristique elit.',
//     Descripcion: 'Cras ornare tristique elit Cras ornare tristique elit Cras ornare tristique elit.',
//     Dimension: 'Diversión',
//     Inicio: '05/03/2025',
//     Fin: '09/03/2025',
//     Estado: `Terminado`,
//     Porcentaje: 100,
//     Actividades: [],
//   },
//   {
//     Nombre: 'Vivamus vestibulum ntulla nec a.',
//     Descripcion: 'Cras ornare tristique elit Cras ornare tristique elit Cras ornare tristique elit.',
//     Dimension: 'Diversión',
//     Inicio: '05/03/2025',
//     Fin: '09/03/2025',
//     Estado: `Nuevo`,
//     Porcentaje: 0,
//     Actividades: [],
//   },
//   {
//     Nombre: 'Praesent placerat risus quis eros.',
//     Descripcion: 'Cras ornare tristique elit Cras ornare tristique elit Cras ornare tristique elit.',
//     Dimension: 'Diversión',
//     Inicio: '05/03/2025',
//     Fin: '09/03/2025',
//     Estado: `Pendiente`,
//     Porcentaje: 20,
//     Actividades: [],
//   },
// ];